import {
  AlertDto,
  GlobalAlertViewDto,
  MemberAlertViewDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { alertRequests } from '../requests/alert.requests';
import { useAppSelector } from '../store-hooks';
import { AlertState } from '../types/alertTypes';
import { createApiThunk } from '../utils/api-thunk';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getGlobalAlerts = createApiThunk(alertRequests.getGlobalAlerts);
export const getAlertsForMember = createApiThunk(
  alertRequests.getAlertsForMember
);
export const deleteAlert = createApiThunk(alertRequests.deleteAlert);

// INITIAL STATE ===============================================================
const alertAdapter = createEntityAdapter<AlertDto>();
const initialState: AlertState = {
  status: 'initial',
  alerts: alertAdapter.getInitialState(),
  globalAlertIds: null,
  memberAlertIds: {},
};

// SLICE =======================================================================
export const alertSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getGlobalAlerts.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getGlobalAlerts.fulfilled,
        (state, action: PayloadAction<GlobalAlertViewDto>) => {
          const { globalAlertIds, alerts } = action.payload;
          alertAdapter.setMany(state.alerts, alerts);
          state.globalAlertIds = globalAlertIds;
          state.status = 'success';
        }
      )
      .addCase(getGlobalAlerts.rejected, state => {
        state.status = 'failed';
      })
      .addCase(getAlertsForMember.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getAlertsForMember.fulfilled,
        (state, action: PayloadAction<MemberAlertViewDto>) => {
          const { memberId, memberAlertIds, alerts } = action.payload;
          alertAdapter.addMany(state.alerts, alerts);
          state.memberAlertIds[memberId] = memberAlertIds;
          state.status = 'success';
        }
      )
      .addCase(getAlertsForMember.rejected, state => {
        state.status = 'failed';
      })
      .addCase(deleteAlert.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        deleteAlert.fulfilled,
        (state, action: PayloadAction<AlertDto>) => {
          const { id, memberId } = action.payload;
          if (memberId) {
            state.memberAlertIds[memberId] = [
              ...state.memberAlertIds[memberId],
            ].filter(i => i !== id);
          } else {
            state.globalAlertIds = [...state.globalAlertIds].filter(
              i => i !== id
            );
          }
          alertAdapter.removeOne(state.alerts, id);
          state.status = 'success';
        }
      )
      .addCase(deleteAlert.rejected, state => {
        state.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const alertReducer = alertSlice.reducer;

// SELECTORS ===================================================================
const selectState = (state): AlertState =>
  (state as { alerts: AlertState })?.alerts;

const selectAlertAdapterState = state =>
  (state as { alerts: AlertState })?.alerts?.alerts;

const selectAlertSliceStatus = () =>
  createSelector(selectState, state => state.status);

const selectGlobalAlerts = createSelector(
  selectState,
  selectAlertAdapterState,
  (state, adapterState) => {
    return state.globalAlertIds?.map(i => adapterState.entities[i]);
  }
);

const selectMemberAlertsByMember = (memberId: string) =>
  createSelector(
    selectState,
    selectAlertAdapterState,
    (state, adapterState) => {
      return state.memberAlertIds[memberId]?.map(i => adapterState.entities[i]);
    }
  );

// EXPORT HELPER ===============================================================
export const alertAppSelectors = {
  useSelectAlertSliceStatus: (): string =>
    useAppSelector(selectAlertSliceStatus()),
  useSelectGlobalAlerts: (): AlertDto[] => useAppSelector(selectGlobalAlerts),
  useSelectMemberAlertsByMember: (memberId: string): AlertDto[] =>
    useAppSelector(selectMemberAlertsByMember(memberId)),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
